/**
 * File auto-completion dropdown for @ symbol file input
 * Shows file suggestions as user types @filename
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { FileText, Folder, FileImage, FileCode, BookOpen } from 'lucide-react'
import { Card } from '../ui/card'
import { Button } from '../ui/button'
import fileDiscoveryService, { FileDiscoveryItem } from '../../services/fileDiscoveryService'

interface FileAutoCompleteProps {
  inputValue: string
  conversationId?: string
  onFileSelect: (file: FileDiscoveryItem) => void
  onClose: () => void
  visible: boolean
  position: { top: number; left: number; showAbove?: boolean }
}

export interface FileAutoCompleteRef {
  selectNext: () => void
  selectPrevious: () => void
  selectCurrent: () => void
  isVisible: () => boolean
}

export const FileAutoComplete = forwardRef<FileAutoCompleteRef, FileAutoCompleteProps>(
  ({ inputValue, conversationId, onFileSelect, onClose, visible, position }, ref) => {
    const [suggestions, setSuggestions] = useState<FileDiscoveryItem[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      selectNext: () => {
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
      },
      selectPrevious: () => {
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
      },
      selectCurrent: () => {
        if (suggestions[selectedIndex]) {
          handleFileSelect(suggestions[selectedIndex])
        }
      },
      isVisible: () => visible && suggestions.length > 0
    }))

    // Load suggestions when input changes
    useEffect(() => {
      if (!visible) {
        setSuggestions([])
        return
      }

      const loadSuggestions = async () => {
        setLoading(true)
        try {
          const result = await fileDiscoveryService.getAutoCompleteSuggestions(
            inputValue,
            { conversationId, maxResults: 10 }
          )
          
          setSuggestions(result.items)
          setHasMore(result.hasMore)
          setSearchTerm(result.searchTerm)
          setSelectedIndex(0) // Reset selection when suggestions change
        } catch (error) {
          console.error('Error loading file suggestions:', error)
          setSuggestions([])
        } finally {
          setLoading(false)
        }
      }

      // Debounce the search
      const timeoutId = setTimeout(loadSuggestions, 150)
      return () => clearTimeout(timeoutId)
    }, [inputValue, conversationId, visible])

    // Handle clicks outside dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          onClose()
        }
      }

      if (visible) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [visible, onClose])

    const handleFileSelect = (file: FileDiscoveryItem) => {
      onFileSelect(file)
      onClose()
    }

    const getFileIcon = (file: FileDiscoveryItem) => {
      if (file.type === 'directory') {
        return <Folder className="h-4 w-4 text-primary" />
      }
      
      const ext = file.extension?.toLowerCase()
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
        return <FileImage className="h-4 w-4 text-green-500" />
      }
      if (ext === 'md' || ext === 'markdown') {
        return <BookOpen className="h-4 w-4 text-primary" />
      }
      if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'css', 'html'].includes(ext || '')) {
        return <FileCode className="h-4 w-4 text-primary" />
      }
      return <FileText className="h-4 w-4 text-gray-500" />
    }

    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const getFileTypeLabel = (file: FileDiscoveryItem) => {
      if (file.type === 'directory') return 'Directory'
      if (!file.extension) return 'File'
      return `${file.extension.toUpperCase()} File`
    }

    if (!visible || suggestions.length === 0) {
      return null
    }

    return (
      <Card
        ref={dropdownRef}
        className={`fixed z-50 w-80 max-h-64 overflow-y-auto border border-border bg-background ${
          position.showAbove ? 'shadow-2xl animate-in slide-in-from-bottom-2' : 'shadow-lg animate-in slide-in-from-top-2'
        }`}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className="p-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
            <div className="text-xs text-muted-foreground">
              File suggestions for: <span className="font-mono">{searchTerm || '@'}</span>
            </div>
            {hasMore && (
              <div className="text-xs text-muted-foreground">
                +{hasMore ? 'more' : ''}
              </div>
            )}
          </div>

          {/* Loading state */}
          {loading && (
            <div className="text-xs text-muted-foreground text-center py-2">
              Loading files...
            </div>
          )}

          {/* Suggestions list */}
          <div className="space-y-1">
            {suggestions.map((file, index) => (
              <Button
                key={file.path}
                variant="ghost"
                size="sm"
                className={`w-full justify-start p-2 h-auto text-left ${
                  index === selectedIndex ? 'bg-accent text-accent-foreground' : ''
                }`}
                onClick={() => handleFileSelect(file)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-start justify-between w-full min-w-0">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="flex-shrink-0 mt-0.5">
                      {getFileIcon(file)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {file.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {getFileTypeLabel(file)}
                        {file.size && ` • ${formatFileSize(file.size)}`}
                      </div>
                      {file.path !== file.name && (
                        <div className="text-xs text-muted-foreground/70 truncate font-mono">
                          {file.path}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Keyboard hint for selected item */}
                  {index === selectedIndex && (
                    <div className="flex-shrink-0 ml-2">
                      <div className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        ↵
                      </div>
                    </div>
                  )}
                </div>
              </Button>
            ))}
          </div>

          {/* Help text */}
          <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>↑↓ Navigate • ↵ Select • Esc Cancel</span>
              {position.showAbove && (
                <span className="text-muted-foreground/50">▲</span>
              )}
              {!position.showAbove && (
                <span className="text-muted-foreground/50">▼</span>
              )}
            </div>
            {hasMore && (
              <div className="text-muted-foreground/70">Type more to refine search</div>
            )}
          </div>
        </div>
      </Card>
    )
  }
)

FileAutoComplete.displayName = 'FileAutoComplete'